import { Head } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'
import { Mail, MessageSquare, Shield, FileText } from 'lucide-react'

const contactChannels = [
  {
    icon: Mail,
    title: 'General Inquiries',
    description: 'Questions about KonexZero, pricing, or partnership opportunities.',
    contact: 'hello@konexzero.com',
    href: 'mailto:hello@konexzero.com',
  },
  {
    icon: MessageSquare,
    title: 'Technical Support',
    description: 'Need help with integration, API issues, or debugging?',
    contact: 'support@konexzero.com',
    href: 'mailto:support@konexzero.com',
  },
  {
    icon: Shield,
    title: 'Security',
    description: 'Report a security vulnerability or ask about our security practices.',
    contact: 'security@konexzero.com',
    href: 'mailto:security@konexzero.com',
  },
  {
    icon: FileText,
    title: 'Legal & Privacy',
    description: 'Data protection requests, legal inquiries, or compliance questions.',
    contact: 'legal@konexzero.com',
    href: 'mailto:legal@konexzero.com',
  },
]

export default function Contact() {
  return (
    <>
      <Head>
        <title>Contact — KonexZero</title>
        <meta name="description" content="Get in touch with KonexZero. General inquiries, technical support, enterprise sales, and security reporting contacts." />
      </Head>
      <PageLayout>
        <div className="space-y-12">
          <PageHeader
            eyebrow="Contact"
            title="Get in Touch"
            description="We're here to help. Reach out to the right team for the fastest response."
          />

          <SectionDivider />

          <div className="grid gap-4 sm:grid-cols-2">
            {contactChannels.map((channel) => {
              const Icon = channel.icon
              return (
                <article key={channel.title} className="surface-card surface-card-hover p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-foreground/[0.04] text-foreground/80">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </div>
                    <h3 className="font-heading text-base font-semibold tracking-tight">
                      {channel.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{channel.description}</p>
                  <a
                    href={channel.href}
                    className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
                  >
                    {channel.contact}
                  </a>
                </article>
              )
            })}
          </div>

          <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Enterprise Sales</h2>
          <p className="text-muted-foreground leading-relaxed">
            Looking for custom pricing, dedicated support, SLA guarantees, or custom deployment
            options? Our enterprise team would love to discuss your requirements.
          </p>
          <a
            href="mailto:sales@konexzero.com"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground shadow-sm h-9 px-4 hover:bg-primary/90 transition-colors"
          >
            Contact Sales
          </a>
        </section>

        <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Developer Resources</h2>
          <p className="text-muted-foreground leading-relaxed">
            Before reaching out, you might find answers in our resources:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <a href="/docs" className="text-primary hover:underline">
                API Documentation
              </a>{' '}
              -- Complete reference for all API endpoints
            </li>
            <li>
              <a href="/security" className="text-primary hover:underline">
                Security Practices
              </a>{' '}
              -- Learn about our security measures
            </li>
            <li>
              <a href="/healthz" className="text-primary hover:underline">
                System Status
              </a>{' '}
              -- Check current platform health
            </li>
          </ul>
        </section>
      </div>
    </PageLayout>
    </>
  )
}
